'use client';

import { WalletClient, P2PKH, PublicKey, Utils, Random } from '@bsv/sdk';

// BRC-29 protocol id (verified from @bsv/sdk source: BasicBRC29). Used to derive
// a unique per-payment key for the recipient identity pubkey.
const BRC29_PROTOCOL_ID: [2, string] = [2, '3241645161d8'];

// Single shared wallet client. 'auto' races all available substrates; the
// second arg is our originator domain, which must match the page origin so
// the wallet's origin-based permissioning attributes actions correctly. Using
// a hardcoded domain breaks localhost/preview deploys (the wallet may silently
// refuse createAction), so derive it from the current host.
let _wallet: WalletClient | null = null;
function originator(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return 'localhost';
}
export function getWallet(): WalletClient {
  if (!_wallet) {
    _wallet = new WalletClient('auto', originator());
  }
  return _wallet;
}

// Probe whether a BRC-100 wallet is reachable in this browser.
export async function detectWallet(): Promise<boolean> {
  try {
    await getWallet().connectToSubstrate();
    return true;
  } catch {
    return false;
  }
}

// Ensure the user is authenticated. Call only in response to an explicit
// user action (Connect click) — waitForAuthentication blocks until the
// wallet's own login/unlock UI completes.
export async function ensureAuthenticated(): Promise<boolean> {
  const wallet = getWallet();
  try {
    const { authenticated } = await wallet.isAuthenticated();
    if (authenticated) return true;
  } catch {
    // fall through to waitForAuthentication
  }
  try {
    await wallet.waitForAuthentication();
    return true;
  } catch {
    return false;
  }
}

// The connected user's identity public key (compressed hex).
export async function getIdentityKey(): Promise<string> {
  const { publicKey } = await getWallet().getPublicKey({ identityKey: true });
  return publicKey;
}

// 'mainnet' | 'testnet' as reported by the connected wallet.
export async function getNetwork(): Promise<string> {
  const { network } = await getWallet().getNetwork();
  return network;
}

/**
 * Map a raw wallet-SDK error to a short, user-readable message.
 *
 * createAction failures arrive as long internal messages (JSON-ish dumps of
 * the action args, fee accounting, etc.) that are useless and alarming when
 * shown verbatim in the purchase UI. The full error should still be logged
 * by the caller for diagnostics.
 */
export function friendlyPaymentError(e: unknown): string {
  const raw = e instanceof Error && e.message ? e.message : typeof e === 'string' ? e : '';
  const lower = raw.toLowerCase();

  if (lower.includes('insufficient funds')) {
    // The SDK reports exactly how short the wallet is - surface that number.
    const short = raw.match(/([\d,]+)\s+more satoshis are needed/i);
    const total = raw.match(/total of\s+([\d,]+)/i);
    const shortSats = short ? Number(short[1].replace(/,/g, '')) : null;
    const totalSats = total ? Number(total[1].replace(/,/g, '')) : null;
    const parts = ['Not enough BSV in your wallet'];
    if (totalSats) parts.push(`this purchase needs about ${totalSats.toLocaleString()} sats including fee`);
    if (shortSats) parts.push(`you are about ${shortSats.toLocaleString()} sats short`);
    return `${parts.join(' — ')}. Top up your wallet and try again.`;
  }

  if (
    lower.includes('denied') ||
    lower.includes('rejected') ||
    lower.includes('cancel') ||
    lower.includes('abort')
  ) {
    return 'Payment cancelled in your wallet.';
  }

  // Unknown failure: keep short messages, but never show internal dumps.
  if (raw && raw.length <= 160) return raw;
  return 'Payment failed — your wallet reported an error. Check your wallet app for details.';
}

export type PaymentResult = {
  txid: string;
  // Signed transaction in BEEF format (byte array) — lets the recipient
  // internalize the payment without a chain lookup.
  tx: number[];
  derivationPrefix: string;
  derivationSuffix: string;
};

// Pay `satoshis` to `recipientIdentityKey` via a unique BRC-29-derived P2PKH
// address. Returns the txid + BEEF + derivation nonces (needed by the
// recipient to internalize/claim the funds).
export async function payIdentityKey(
  recipientIdentityKey: string,
  satoshis: number,
  description: string
): Promise<PaymentResult> {
  if (!Number.isInteger(satoshis) || satoshis <= 0) {
    throw new Error(`Invalid payment amount: ${satoshis} sats`);
  }
  const wallet = getWallet();

  const derivationPrefix = Utils.toBase64(Random(8));
  const derivationSuffix = Utils.toBase64(Random(8));
  const keyID = `${derivationPrefix} ${derivationSuffix}`;

  // Derive the recipient's per-payment public key.
  const { publicKey } = await wallet.getPublicKey({
    protocolID: BRC29_PROTOCOL_ID,
    keyID,
    counterparty: recipientIdentityKey,
  });

  const address = PublicKey.fromString(publicKey).toAddress('mainnet');
  const lockingScript = new P2PKH().lock(address).toHex();

  const { txid, tx } = await wallet.createAction({
    description,
    outputs: [
      {
        satoshis,
        lockingScript,
        outputDescription: 'BRC-29 payment',
        customInstructions: JSON.stringify({
          derivationPrefix,
          derivationSuffix,
        }),
      },
    ],
    options: { randomizeOutputs: false },
  });

  if (!txid || !tx) {
    throw new Error('createAction returned no transaction');
  }

  // AtomicBEEF may be a Uint8Array in sdk v2 — normalize to a plain number
  // array so it JSON-serializes as [n, n, ...] rather than an object.
  return { txid, tx: Array.from(tx), derivationPrefix, derivationSuffix };
}

// Internalize (take custody of) an incoming BRC-29 payment addressed to this
// wallet's identity key — e.g. a ladder-claim payout from the platform's
// server wallet. `tx` is the AtomicBEEF byte array; the derivation nonces and
// senderIdentityKey come from the payer. Returns false on any failure (the
// BEEF + nonces remain valid, so a retry or manual recovery is possible).
export async function internalizeIncomingPayment(args: {
  tx: number[];
  senderIdentityKey: string;
  derivationPrefix: string;
  derivationSuffix: string;
  description: string;
}): Promise<boolean> {
  try {
    const { accepted } = await getWallet().internalizeAction({
      tx: args.tx,
      outputs: [
        {
          outputIndex: 0,
          protocol: 'wallet payment',
          paymentRemittance: {
            senderIdentityKey: args.senderIdentityKey,
            derivationPrefix: args.derivationPrefix,
            derivationSuffix: args.derivationSuffix,
          },
        },
      ],
      description: args.description,
    });
    return accepted === true;
  } catch (e) {
    console.error('[wallet] internalizeAction failed:', e);
    return false;
  }
}
